import { useRef } from 'react';
import { motion as Motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

/**
 * Shared app card component used on both the main landing page and the CRE8APPS page.
 *
 * Props:
 *   item       — app data object from cre8Apps.js (desc can be string or { de, en })
 *   lang       — 'de' | 'en'
 *   color      — accent color string (CSS var or hex)
 *   onPreview  — optional callback(item); when provided clicking the image opens a full-size preview
 *   onAction   — optional callback(item); when provided renders the CTA button + status badge
 *   copy       — optional { inProgress, cta, demo } for button labels (required if onAction set)
 */
const AppCard = ({ item, lang, color, onPreview, onAction, copy, hoverTextDark, statusTextColor = 'var(--bg-color)', freeRibbonBgColor = '#f41465', freeRibbonTextColor = '#ffffff' }) => {
  const ref = useRef(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const { scrollYProgress } = useScroll({
    target: isMobile ? null : ref,
    offset: ['start end', 'end start'],
  });
  
  const opacity = useTransform(scrollYProgress, [0, 0.12, 0.88, 1], [0, 1, 1, 0]);
  const y = useTransform(scrollYProgress, [0, 0.12], [28, 0]);

  const desc = typeof item.desc === 'object'
    ? (item.desc[lang] ?? item.desc.de)
    : item.desc;
  const showFreeRibbon = item.id === 'audiobuster' || item.id === 'qr2go';

  const handleTrackClick = () => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'app_click', {
        app_id: item.id,
        app_title: item.title,
        status: item.status
      });
    }
  };

  return (
    <Motion.div
      ref={ref}
      style={{ opacity, y, '--card-color': color }}
      className="group outline outline-1 outline-border-color/20 bg-surface-color/20 rounded-[4px] flex flex-col overflow-hidden hover:outline-[5px] hover:[outline-color:var(--card-color)] transition-all duration-300"
    >
      {/* Image */}
      {onPreview ? (
        <div
          className="aspect-video overflow-hidden relative w-full cursor-pointer"
          onClick={() => {
            handleTrackClick();
            if (item.status === 'live' && item.url) {
              window.open(item.url, '_blank', 'noopener,noreferrer');
            } else if (onAction) {
              onAction(item);
            }
          }}
        >
          <img
            src={item.image}
            alt={`CRE8NOW: ${item.title}`}
            width={800}
            height={450}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000"
          />
          {showFreeRibbon && (
            <div
              className="absolute top-5 right-[-42px] z-20 w-40 rotate-45 py-1 text-center text-[10px] font-black uppercase shadow-lg"
              style={{ backgroundColor: freeRibbonBgColor, color: freeRibbonTextColor }}
            >
              100% Free
            </div>
          )}
          {onAction && copy && item.status === 'in-progress' && (
            <span
              className="absolute top-0 right-0 z-20 block backdrop-blur-md text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-[4px] leading-none"
              style={{ backgroundColor: color, color: statusTextColor }}
            >
              {copy.inProgress}
            </span>
          )}
        </div>
      ) : (
        <div
          className="aspect-video overflow-hidden relative cursor-pointer"
          onClick={() => {
            if (item.status === 'live' && item.url) {
              window.open(item.url, '_blank', 'noopener,noreferrer');
            } else if (onAction) {
              onAction(item);
            }
          }}
        >
          <img
            src={item.image}
            alt={`CRE8NOW: ${item.title}`}
            width={800}
            height={450}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000"
          />
          {showFreeRibbon && (
            <div
              className="absolute top-5 right-[-42px] z-20 w-40 rotate-45 py-1 text-center text-[10px] font-black uppercase shadow-lg"
              style={{ backgroundColor: freeRibbonBgColor, color: freeRibbonTextColor }}
            >
              100% Free
            </div>
          )}
          {onAction && copy && item.status === 'in-progress' && (
            <span
              className="absolute top-0 right-0 z-20 block backdrop-blur-md text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-bl-[4px] leading-none"
              style={{ backgroundColor: color, color: statusTextColor }}
            >
              {copy.inProgress}
            </span>
          )}
        </div>
      )}

      {/* Content */}
      <div
        className="p-10 md:p-12 flex flex-col gap-4 flex-1 cursor-pointer"
        onClick={() => {
          if (item.status === 'live' && item.url) {
            window.open(item.url, '_blank', 'noopener,noreferrer');
          } else if (onAction) {
            onAction(item);
          }
        }}
      >
        <div>
          <h6 className="text-text-muted mb-3">{item.subtitle}</h6>
          <h4
            className="font-black tracking-tight leading-none mb-4"
          >
            {item.title}
          </h4>
          <p className="text-text-muted font-light leading-relaxed">{desc}</p>
        </div>

        {/* Tags */}
        <div className="flex flex-wrap gap-2 mt-auto">
          {item.tags.map(tag => (
            <span
              key={tag}
              className="text-[10px] font-bold uppercase tracking-[0.2em] px-3 py-1.5 border border-border-color rounded-[4px] text-text-muted"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* CTA button — only when onAction provided */}
        {onAction && copy && (
          <div>
            {item.status === 'live' && item.url ? (
              <a
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-m group/btn relative px-6 py-3.5 font-black uppercase tracking-widest text-[10px] transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px] self-start inline-flex items-center"
              >
                <div
                  className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 group-hover:translate-y-0 transition-transform duration-500"
                  style={{ backgroundColor: color }}
                />
                <span className={`relative z-10 flex items-center transition-colors duration-500 ${hoverTextDark ? 'group-hover/btn:text-black group-hover:text-black' : 'group-hover/btn:text-white group-hover:text-white'}`}>
                  {copy.cta}
                  <span className="w-0 opacity-0 group-hover/btn:w-5 group-hover/btn:ml-3 group-hover/btn:opacity-100 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                    <ArrowRight size={14} />
                  </span>
                </span>
              </a>
            ) : (
              <button
                onClick={() => onAction(item)}
                className="cta-m group/btn relative px-6 py-3.5 font-black uppercase tracking-widest text-[10px] transition-all duration-500 overflow-hidden bg-text-color text-bg-color rounded-[4px] self-start inline-flex items-center"
              >
                <div
                  className="absolute inset-0 translate-y-full group-hover/btn:translate-y-0 group-hover:translate-y-0 transition-transform duration-500"
                  style={{ backgroundColor: color }}
                />
                <span className={`relative z-10 flex items-center transition-colors duration-500 ${hoverTextDark ? 'group-hover/btn:text-black group-hover:text-black' : 'group-hover/btn:text-white group-hover:text-white'}`}>
                  {item.status === 'in-progress' ? copy.demo : copy.cta}
                  <span className="w-0 opacity-0 group-hover/btn:w-5 group-hover/btn:ml-3 group-hover/btn:opacity-100 group-hover:w-5 group-hover:ml-3 group-hover:opacity-100 transition-all duration-500 overflow-hidden flex items-center">
                    <ArrowRight size={14} />
                  </span>
                </span>
              </button>
            )}
          </div>
        )}
      </div>
    </Motion.div>
  );
};

export default AppCard;
